import {
  addP2PContactFromInviteToken,
  deleteAllP2PContactsDocs,
  deleteP2PContactDocument,
  fetchP2PContactsOnce,
  removeChatContactLoose as removeP2PContactLooseFirestore,
  renameChatContact as renameP2PContactFirestore,
  setChatContactArchived as setChatContactArchivedFirestore,
  subscribeChatContacts,
} from './chatContactsFirestore';
import { clearSessionChatKeyPair } from './chatCryptoKeys';
import { clearLocalOnlyMasterKeySession } from './chatLocalMasterKey';
import {
  addHandshakeIgnoreInviteToken,
  removeHandshakeIgnoreInviteToken,
} from './chatHandshakeIgnoreFirestore';
import { signalUserDeletedChatThread, tryPurgeP2PThreadIfBothDeleted } from './chatMessagesFirestore';
import { p2pThreadId } from './p2pThreadInvitePath';
import { clearSessionInviteToken, getSessionInviteToken } from './chatSessionIdentity';

export type { ChatContact } from './chatContactsFirestore';

export {
  subscribeChatContacts,
  fetchP2PContactsOnce,
  deleteAllP2PContactsDocs,
};

/** Миграция контактов из localStorage отключена: список только в Firestore. */
export async function migrateLocalP2PContactsToFirestore(_userId: string): Promise<void> {
  /* no-op */
}

/** @deprecated флага в localStorage больше нет */
export function clearP2PContactsMigrationFlag(_userId: string): void {
  /* no-op */
}

/**
 * После «Удалить чат» не поднимать контакт снова из `chatInviteHandshakes`.
 */
export async function ignoreHandshakeReaddForContact(userId: string, inviteToken: string): Promise<void> {
  await addHandshakeIgnoreInviteToken(userId, inviteToken);
}

export async function clearHandshakeIgnoreForContact(userId: string, inviteToken: string): Promise<void> {
  await removeHandshakeIgnoreInviteToken(userId, inviteToken);
}

export function buildInviteUrl(origin: string, token: string): string {
  return `${origin}/app/chats?invite=${encodeURIComponent(token)}`;
}

export function isDefaultChatContactName(name: string, inviteToken: string): boolean {
  return name === `Contact ${inviteToken.slice(0, 8)}…`;
}

export async function addContactFromInviteToken(
  inviteToken: string | null,
  userId: string
): Promise<'added' | 'exists' | 'self' | 'invalid'> {
  const my = getSessionInviteToken(userId);
  if (!my) return 'invalid';
  return addP2PContactFromInviteToken(inviteToken, userId, my);
}

export async function renameChatContact(userId: string, contactId: string, newName: string): Promise<boolean> {
  return renameP2PContactFirestore(userId, contactId, newName);
}

export async function archiveChatContact(userId: string, contactInviteToken: string): Promise<boolean> {
  return setChatContactArchivedFirestore(userId, contactInviteToken, true);
}

export async function unarchiveChatContact(userId: string, contactInviteToken: string): Promise<boolean> {
  return setChatContactArchivedFirestore(userId, contactInviteToken, false);
}

export async function removeChatContactLoose(userId: string, contactId: string): Promise<void> {
  return removeP2PContactLooseFirestore(userId, contactId);
}

/**
 * Убрать чат из аккаунта навсегда (не только архив): сигнал удаления треда, попытка совместной очистки,
 * удаление документа `p2pContacts`, игнор handshake для повторного автодобавления.
 */
export async function permanentlyDeleteChatContact(
  userId: string,
  peerInviteToken: string,
  myInviteToken: string | null | undefined
): Promise<void> {
  const peer = peerInviteToken.trim();
  if (!userId || !peer) return;
  await ignoreHandshakeReaddForContact(userId, peer);
  const my = typeof myInviteToken === 'string' ? myInviteToken.trim() : '';
  if (my && my !== peer) {
    const threadId = p2pThreadId(my, peer);
    try {
      await signalUserDeletedChatThread({
        threadId,
        deletedByInviteToken: my,
        deletedByUid: userId,
      });
      await tryPurgeP2PThreadIfBothDeleted(threadId, userId);
    } catch {
      /* сеть / правила */
    }
  }
  await deleteP2PContactDocument(userId, peer);
}

export function clearChatLocalStateForUser(uid: string): void {
  clearSessionInviteToken(uid);
  clearSessionChatKeyPair(uid);
  clearLocalOnlyMasterKeySession(uid);
}
